import {TYPESENSE_PRIMARY_CLUSTER_HOST} from "@buildwithflux/constants";
import {createTypesenseClient} from "@buildwithflux/search";
import {useEffect, useState} from "react";
import type {Client} from "typesense";

import {getCurrentIdToken} from "../firebase/firebaseClient";
import {getAccountsApiBase} from "../utils/apiBase";
import {fetchWithRetry} from "../utils/fetchWithRetry";

// Cloud cluster host, used when the auth endpoint falls back to a cloud key
// (environments without a primary-cluster search-only key configured).
const TYPESENSE_CLOUD_HOST = "8iq4spr5t63o1vlgp.a1.typesense.net";

interface AccountsViewAuthResponse {
    scopedSearchKey: string;
    collection: string;
    cluster: "cloud" | "primary";
}

export interface AccountsViewClient {
    client: Client;
    collection: string;
}

/**
 * Tier-aware accounts-view Typesense client for user/org search. The auth
 * endpoint scopes the key and picks the collection from the bearer token:
 * `accounts-view-public` for anonymous viewers, `accounts-view-users` for
 * signed-in viewers (so visibility-gated fields project per viewer).
 *
 * Re-fetches whenever `viewerUid` changes so the tier follows auth state. Pass
 * `enabled: false` on surfaces that never search accounts, to skip the auth call.
 */
export function useAccountsViewClient(viewerUid: string | null, enabled = true): AccountsViewClient | null {
    const [result, setResult] = useState<AccountsViewClient | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;

        void (async () => {
            try {
                const token = await getCurrentIdToken();
                const res = await fetchWithRetry(() =>
                    fetch(`${getAccountsApiBase()}/accounts/auth/typesense-accounts`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? {Authorization: `Bearer ${token}`} : {}),
                        },
                        body: JSON.stringify({}),
                    }),
                );
                if (!res.ok) {
                    throw new Error(`Accounts-view auth failed: ${res.status}`);
                }
                const data = (await res.json()) as AccountsViewAuthResponse;
                if (cancelled) return;
                setResult({
                    client: createTypesenseClient({
                        host: data.cluster === "primary" ? TYPESENSE_PRIMARY_CLUSTER_HOST : TYPESENSE_CLOUD_HOST,
                        port: 443,
                        protocol: "https",
                        apiKey: data.scopedSearchKey,
                        cacheSearchResultsForSeconds: 10,
                    }),
                    collection: data.collection,
                });
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("Failed to initialize accounts-view client:", err);
                setResult(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [viewerUid, enabled]);

    return result;
}
